import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Shield, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../lib/AuthContext';
import { Header } from './Header';
import { Stats } from './components/Stats';
import { Filters } from './Filters';
import { RequestCard } from './RequestCard';
import { HiddenRequests } from './HiddenRequests';
import { HideConfirmationDialog } from './HideConfirmationDialog';
import { useRequests } from './hooks/useRequests';
import { useHiddenRequests } from './hooks/useHiddenRequests';
import { LoadingIndicator } from './LoadingIndicator';

export function InsurerDashboard() {
  const [filter, setFilter] = useState<'all' | 'new' | 'active' | 'expired' | 'won'>('all');
  const [bidAmounts, setBidAmounts] = useState<{ [key: string]: string }>({});
  const [bidError, setBidError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<{ [key: string]: boolean }>({});
  const { user, signOut } = useAuth();
  const [insurerName, setInsurerName] = useState<string>('');
  const [showHiddenRequests, setShowHiddenRequests] = useState(false);
  const [requestToHide, setRequestToHide] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<Record<string, any> | null>(null);
  const [regionFilter, setRegionFilter] = useState('');
  const [ageRangeFilter, setAgeRangeFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { requests, loading: requestsLoading, error, refresh } = useRequests(user?.id);
  const { 
    hiddenRequests, 
    hiddenRequestsList, 
    loading: loadingHidden,
    fetchHiddenRequests,
    fetchHiddenRequestsSet
  } = useHiddenRequests(user?.id);

  // Fetch insurer name
  useEffect(() => {
    async function fetchInsurerName() {
      if (!user?.id) return;
      
      const { data, error } = await supabase
        .from('insurers')
        .select('name')
        .eq('id', user.id)
        .single();

      if (!error && data) {
        setInsurerName(data.name);
      }
    }
    
    fetchInsurerName();
  }, [user?.id]);

  // Fetch hidden requests
  useEffect(() => {
    if (showHiddenRequests) {
      fetchHiddenRequests();
    }
  }, [showHiddenRequests]);

  const handleRefresh = async (setLoadingState = false) => {
    setIsRefreshing(true);
    if (setLoadingState) {
      setIsRefreshing(true);
    }
    await refresh();
    setIsRefreshing(false);
  };

  async function hideRequest(requestId: string) {
    if (!requestId) return;

    try {
      const { error } = await supabase.rpc(
        'toggle_request_visibility',
        { 
          p_request_id: requestId,
          p_hide: true
        }
      );

      if (error) throw error;
      await fetchHiddenRequestsSet();
      await refresh();
      setRequestToHide(null);
    } catch (error) {
      console.error('Error hiding request:', error);
    }
  }

  async function unhideRequest(requestId: string) {
    try {
      const { error } = await supabase.rpc(
        'toggle_request_visibility',
        { 
          p_request_id: requestId,
          p_hide: false
        }
      );

      if (error) throw error;
      await fetchHiddenRequests();
      await refresh();
    } catch (error) {
      console.error('Error unhiding request:', error);
    }
  }

  async function handleBidSubmit(requestId: string) {
    const amount = parseFloat(bidAmounts[requestId]);
    if (isNaN(amount) || amount <= 0) {
      setBidError('Bid amount must be greater than zero');
      return;
    }

    setSubmitting({ ...submitting, [requestId]: true });
    setBidError(null);

    try {
      const { error } = await supabase.rpc(
        'add_insurer_bid',
        {
          p_request_id: requestId,
          p_insurer_id: user?.id,
          p_amount: amount
        }
      );

      if (error) throw error;
      setBidAmounts({ ...bidAmounts, [requestId]: '' });
      refresh();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit bid';
      setBidError(errorMessage);
      console.error('Bid submission error:', err);
    } finally {
      setSubmitting({ ...submitting, [requestId]: false });
    }
  }

  function getRemainingTime(expiresAt: string): string {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry.getTime() - now.getTime();
    
    if (diff <= 0) {
      return 'Expired';
    }
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    if (days > 0) {
      return `${days}d ${hours}h remaining`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s remaining`;
    }
    return `${seconds}s remaining`;
  }

  if (requestsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingIndicator message="Loading insurance requests..." size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center text-red-700">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          {error}
        </div>
      </div>
    );
  }

  const stats = {
    awaitingBids: requests.filter(r => r.bid_status === 'awaiting_bids' || r.bid_status === 'no_bids').length,
    inProgress: requests.filter(r => r.bid_status === 'bidding_in_progress').length,
    noBidsFromUs: requests.filter(r => !r.has_user_bid && new Date(r.expires_at) > new Date()).length
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        insurerName={insurerName}
        userEmail={user?.email || ''}
        requestCount={requests.length}
        filter={filter}
        isRefreshing={isRefreshing}
        showHiddenRequests={showHiddenRequests}
        onFilterChange={setFilter}
        onToggleHidden={() => setShowHiddenRequests(!showHiddenRequests)}
        onRefresh={handleRefresh}
        onSignOut={signOut}
        stats={stats}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {showHiddenRequests ? (
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Hidden Requests</h2>
            <HiddenRequests
              requests={hiddenRequestsList}
              onUnhide={unhideRequest}
              getRemainingTime={getRemainingTime}
              loading={loadingHidden}
            />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Insurance Requests Available</h2>
            <p className="text-gray-600">There are currently no pending or active insurance requests to bid on.</p>
          </div>
        ) : (
          <div>
            <Stats {...stats} />
            <Filters
              mainFilter={filter}
              region={regionFilter}
              ageRange={ageRangeFilter}
              gender={genderFilter}
              onMainFilterChange={setFilter}
              onRegionChange={setRegionFilter}
              onAgeRangeChange={setAgeRangeFilter}
              onGenderChange={setGenderFilter}
              onClearFilters={() => {
                setFilter('all');
                setRegionFilter('');
                setAgeRangeFilter('');
                setGenderFilter('');
              }}
            />
            <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mt-6">
              {requests.filter(request => {
                const isExpired = new Date(request.expires_at) < new Date();
                
                // Apply main filter
                let passesMainFilter = true;
                switch (filter) {
                  case 'no_bids_from_us':
                    passesMainFilter = !request.has_user_bid && !isExpired;
                    break;
                  case 'new':
                    passesMainFilter = (request.bid_status === 'awaiting_bids' || request.bid_status === 'no_bids') && !isExpired;
                    break;
                  case 'active':
                    passesMainFilter = request.bid_status === 'bidding_in_progress' && !isExpired;
                    break;
                  case 'expired':
                    passesMainFilter = isExpired;
                    break;
                  case 'won':
                    passesMainFilter = isExpired && request.is_lowest_bidder;
                    break;
                }

                // Apply additional filters
                const passesRegionFilter = !regionFilter || request.region === regionFilter;
                const passesAgeFilter = !ageRangeFilter || (() => {
                  const age = request.age;
                  switch (ageRangeFilter) {
                    case '17-25': return age >= 17 && age <= 25;
                    case '26-35': return age >= 26 && age <= 35;
                    case '36-45': return age >= 36 && age <= 45;
                    case '46-55': return age >= 46 && age <= 55;
                    case '56-65': return age >= 56 && age <= 65;
                    case '66+': return age >= 66;
                    default: return true;
                  }
                })();
                const passesGenderFilter = !genderFilter || request.gender === genderFilter;

                return passesMainFilter && passesRegionFilter && passesAgeFilter && passesGenderFilter;
              }).map((request) => (
                <RequestCard
                  key={request.id}
                  request={request}
                  onHide={() => setRequestToHide(request.id)}
                  onSubmitBid={handleBidSubmit}
                  bidAmount={bidAmounts[request.id] || ''}
                  onBidAmountChange={(id, amount) => setBidAmounts({ ...bidAmounts, [id]: amount })}
                  isSubmitting={submitting[request.id] || false}
                  onViewDetails={setSelectedRequest}
                  isHidden={hiddenRequests.has(request.id)}
                  error={bidError}
                  getRemainingTime={getRemainingTime}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {requestToHide && (
        <HideConfirmationDialog
          request={requests.find(r => r.id === requestToHide)}
          onConfirm={() => hideRequest(requestToHide)}
          onCancel={() => setRequestToHide(null)}
          isHidden={hiddenRequests.has(requestToHide)}
        />
      )}
    </div>
  );
}